import React, { useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
} from "@mui/material";
import { getUserDateFormat, getUserTimeFormat } from "../../utils/formatDate";
import { format } from "date-fns";
import useCurrentTripStartStopLogs from "../../hooks/useCurrentTripStartStopLogs";

const TripStatusBadge = ({ status }) => {
  const map = {
    IN_PROGRESS: { label: "In Progress", color: "primary" },
    COMPLETED: { label: "Trip Completed", color: "success" },
    NO_DATA: { label: "No Data", color: "default" },
    PENDING_ORIGIN: { label: "Awaiting Start", color: "warning" },
  };
  const cfg = map[status] ?? { label: status ?? "—", color: "default" };
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      sx={{ fontWeight: 600, fontSize: "11px", height: "24px" }}
    />
  );
};

const TripSegmentTracker = ({ vehicleId, vehicleNumber, stops = [], t }) => {
  const [showOnlyMoving, setShowOnlyMoving] = useState(false);

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const organizationId = parsedData?.user?.organizationId;

  const {
    logs: apiLogs,
    loading,
    tripStatus,
    routeInfo,
    lastFetch,
  } = useCurrentTripStartStopLogs(
    vehicleId,
    organizationId,
    30000,
    !!vehicleId && !!organizationId,
  );

  const buildSegments = useCallback((logs) => {
    if (!logs || logs.length === 0) return [];

    // Normalise type to uppercase
    const normalised = logs.map((l) => ({
      ...l,
      type: (l.type ?? "").toUpperCase(),
    }));

    // Build pairs: each row = one START + its matching STOP
    // Walk through and pair consecutive START→STOP into a "moving" segment,
    // and STOP→next START as a "stopped" (idle) segment between movements.
    const segments = [];
    let i = 0;

    // If data starts with a STOP, skip it
    while (i < normalised.length && normalised[i].type !== "START") i++;

    while (i < normalised.length) {
      const startLog = normalised[i];
      if (startLog.type !== "START") {
        i++;
        continue;
      }

      // Find the very next STOP
      const stopLog =
        normalised[i + 1]?.type === "STOP" ? normalised[i + 1] : null;

      const endTime = stopLog?.timestamp ?? null;
      const endLat = stopLog?.location?.latitude ?? null;
      const endLng = stopLog?.location?.longitude ?? null;
      const duration = stopLog
        ? Math.round(
            (new Date(stopLog.timestamp) - new Date(startLog.timestamp)) / 1000,
          )
        : null;

      // Skip zero-duration pairs (same timestamp = bad data); also skip their idle segment
      if (duration === null || duration > 0) {
        segments.push({
          id: `seg_move_${startLog.timestamp}_${i}`,
          type: "moving",
          reason: startLog.reason ?? null,
          startTime: startLog.timestamp,
          endTime,
          startLat: startLog.location?.latitude ?? null,
          startLng: startLog.location?.longitude ?? null,
          endLat,
          endLng,
          duration,
          status: !endTime ? "ongoing" : "completed",
        });

        // If there's a STOP, also build an idle segment until the next START
        if (stopLog) {
          const nextStart =
            normalised[i + 2]?.type === "START" ? normalised[i + 2] : null;
          const idleEnd = nextStart?.timestamp ?? null;
          const idleDuration = idleEnd
            ? Math.round(
                (new Date(idleEnd) - new Date(stopLog.timestamp)) / 1000,
              )
            : null;

          if (idleDuration === null || idleDuration > 0) {
            segments.push({
              id: `seg_stop_${stopLog.timestamp}_${i}`,
              type: "stopped",
              reason: stopLog.reason ?? null,
              startTime: stopLog.timestamp,
              endTime: idleEnd,
              startLat: stopLog.location?.latitude ?? null,
              startLng: stopLog.location?.longitude ?? null,
              endLat: stopLog.location?.latitude ?? null,
              endLng: stopLog.location?.longitude ?? null,
              duration: idleDuration,
              status: !idleEnd ? "ongoing" : "completed",
            });
          }
        }
      }

      // Advance: consume START + STOP if both exist, else just START
      i += stopLog ? 2 : 1;
    }

    return segments;
  }, []);

  const allSegments = useCallback(() => {
    const segs = buildSegments(apiLogs);
    return segs.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
  }, [apiLogs, buildSegments])();

  const filteredSegments = (
    showOnlyMoving
      ? allSegments.filter((s) => s.type === "moving")
      : allSegments
  ).filter(
    (s) => s.status === "ongoing" || (s.duration != null && s.duration > 0),
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return "Ongoing";
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const userTimeFormat = getUserTimeFormat() ?? "12hr";
    if (userTimeFormat === "24hr") {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHrs = hours % 12 || 12;
    return `${String(displayHrs).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    try {
      const dateObj = new Date(timestamp);
      const userDateFormat = getUserDateFormat();
      const fmt =
        {
          "MM/DD/YYYY": "MM/dd/yyyy",
          "YYYY/MM/DD": "yyyy/MM/dd",
          "DD/MM/YYYY": "dd/MM/yyyy",
        }[userDateFormat] ?? "dd/MM/yyyy";
      return format(dateObj, fmt);
    } catch {
      return new Date(timestamp).toLocaleDateString();
    }
  };

  const formatTimeRange = (startTime, endTime) =>
    `${formatTime(startTime)} — ${endTime ? formatTime(endTime) : "Ongoing"}`;

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const calcDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2);
  };

  // Returns the name of the nearest stop to given coordinates, or null
  const findNearestStop = (lat, lng) => {
    if (!lat || !lng || !stops || stops.length === 0) return null;
    let nearest = null;
    let minDist = Infinity;
    stops.forEach((stop) => {
      const sLat = parseFloat(stop.latitude ?? stop.lat);
      const sLng = parseFloat(stop.longitude ?? stop.lng);
      if (isNaN(sLat) || isNaN(sLng)) return;
      const R = 6371000; // metres
      const dLat = ((sLat - lat) * Math.PI) / 180;
      const dLon = ((sLng - lng) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat * Math.PI) / 180) *
          Math.cos((sLat * Math.PI) / 180) *
          Math.sin(dLon / 2) ** 2;
      const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      if (dist < minDist) {
        minDist = dist;
        nearest = { name: stop.name, dist };
      }
    });
    // Only use if within 1 km
    return nearest && nearest.dist <= 1000 ? nearest.name : null;
  };

  // Returns a human-readable label for a location:
  // priority → nearest stop name → coordinates
  const stopDisplay = (lat, lng) => {
    const nearestName = findNearestStop(lat, lng);
    if (nearestName) return nearestName;
    if (lat == null || lng == null) return "—";
    return `${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}`;
  };

  const calcCumulativeHours = (currentIndex) => {
    let totalSec = 0;
    for (let i = filteredSegments.length - 1; i >= currentIndex; i--) {
      const seg = filteredSegments[i];
      if (seg.type !== "moving") continue;
      if (seg.duration) {
        totalSec += seg.duration;
      } else if (seg.status === "ongoing") {
        totalSec += Math.round(
          (Date.now() - new Date(seg.startTime).getTime()) / 1000,
        );
      }
    }
    return (totalSec / 3600).toFixed(2);
  };

  const locDisplay = (lat, lng) => {
    if (lat == null || lng == null) return "—";
    return `${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        p: 2,
        backgroundColor: "#FFFFFF",
        borderRadius: "10px",
        border: "1px solid #A5DCF3",
        boxShadow: "-2px 1px 5px 1px #00000040",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* {tripStatus && <TripStatusBadge status={tripStatus} />}

          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="caption" color="textSecondary">
                Syncing…
              </Typography>
            </Box>
          )}

          {routeInfo?.routeName && (
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                background: "#F0F4FA",
                borderRadius: "12px",
                px: 1.2,
                py: 0.3,
                border: "1px solid #D0DCF0",
              }}
            >
              {routeInfo.routeName}&nbsp;·&nbsp;
              {routeInfo.direction === "origin_to_destination"
                ? "Outbound"
                : "Inbound"}
            </Typography>
          )} */}
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={showOnlyMoving}
              onChange={(e) => setShowOnlyMoving(e.target.checked)}
              color="primary"
            />
          }
          label={t("Show Only Moving Trips")}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{ boxShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {[
                { label: "Sr. No", width: "60px" },
                { label: "Vehicle Number" },
                { label: "Date" },
                { label: "Time" },
                { label: "Duration" },
                { label: "Cumulative Time (hrs)" },
                { label: "Start Location", minW: "200px" },
                { label: "Stop Location", minW: "200px" },
                { label: "Distance (km)" },
              ].map(({ label, width, minW }) => (
                <TableCell
                  key={label}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#E8EAEE",
                    color: "#183059",
                    ...(width && { width }),
                    ...(minW && { minWidth: minW }),
                  }}
                >
                  {t(label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && filteredSegments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    {t("Loading current trip data")}…
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredSegments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    {tripStatus === "PENDING_ORIGIN"
                      ? "Vehicle has not reached the trip origin yet."
                      : tripStatus === "COMPLETED"
                        ? "Trip completed. Waiting for next trip to begin."
                        : "No trip data yet. Vehicle movement will appear here."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSegments.map((seg, index) => {
                const isOngoing = seg.status === "ongoing";
                const isMoving = seg.type === "moving";
                const serialNo = filteredSegments.length - index;
                const dist = calcDistance(
                  seg.startLat,
                  seg.startLng,
                  seg.endLat,
                  seg.endLng,
                );

                return (
                  <TableRow
                    key={seg.id}
                    sx={{
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      backgroundColor: isMoving ? "#E8F5E9" : "#FFCDD2",
                    }}
                  >
                    <TableCell>{serialNo}</TableCell>

                    <TableCell>
                      <Typography fontWeight={500}>
                        {vehicleNumber || "N/A"}
                      </Typography>
                    </TableCell>

                    <TableCell>{formatDate(seg.startTime)}</TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatTimeRange(seg.startTime, seg.endTime)}
                    </TableCell>

                    <TableCell>
                      <Typography
                        fontWeight={isOngoing ? 600 : 400}
                        color={isOngoing ? "primary" : "inherit"}
                      >
                        {seg.duration
                          ? formatDuration(seg.duration)
                          : isOngoing
                            ? "In Progress"
                            : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography fontWeight={500}>
                        {calcCumulativeHours(index)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                        {stopDisplay(seg.startLat, seg.startLng)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                        {stopDisplay(seg.endLat, seg.endLng)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography fontWeight={500}>{dist ?? "—"}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {lastFetch && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "#aaa", textAlign: "right" }}
        >
          Last synced: {lastFetch.toLocaleTimeString()}
        </Typography>
      )}
    </Box>
  );
};

export default TripSegmentTracker;
